import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useAIPromptField } from '../../../../../shared/components/ai/AIPromptField';
import { SaveStatus } from '../../../../../shared/components/automation/SaveStatus';
import { AIEmailStepForm, AI_EMAIL_PROMPT_MAX_LENGTH } from '../../components/AIEmailStepForm';
import { ConditionalOptionsSection } from '../../components/ConditionalOptionsSection';
import { EmailLikeStepAdvancedOptions } from '../../components/EmailLikeStepAdvancedOptions';
import { ScheduleEditor } from '../../components/ScheduleEditor';
import { useAutomationFlowContext } from '../../contexts/AutomationFlowContext';
import { useAIEmailStepSave } from '../../hooks/useAIEmailStepSave';
import { useAIEmailStepState } from '../../hooks/useAIEmailStepState';
import { useBehaviorConditionalOptions } from '../../hooks/useBehaviorConditionalOptions';
import type { AutomationStepNodeData } from '../../types/stepNodes';
import { useStepRenderer } from './hooks/useStepRenderer';
import { rendererContainer } from './styles/commonRendererStyles';

interface AIEmailStepRendererProps {
	nodeId: string;
	data: AutomationStepNodeData;
	readOnly?: boolean;
}

export function AIEmailStepRenderer({ nodeId, data, readOnly: readOnlyProp }: AIEmailStepRendererProps) {
	const {
		step: aiEmailStep,
		template,
		canEdit,
		readonly,
		trigger,
		errorMessages,
		stepData,
		stepId,
		impersonationContext,
	} = useStepRenderer<Api.IAIEmailAutomationStep>(data, readOnlyProp);

	const { updateNodeData, connectionTypes } = useAutomationFlowContext();

	const stepType = data.stepType;
	const schedule = aiEmailStep?.schedule || stepData?.schedule;

	const {
		name,
		setName,
		prompt,
		setPrompt,
		tagToAddOnClick,
		setTagToAddOnClick,
		showEmailLinkClickTags,
		setShowEmailLinkClickTags,
		skipStepContactFilter,
		setSkipStepContactFilter,
		advancedOptionsToggled,
		setAdvancedOptionsToggled,
		showRequiredFieldError,
		autoSave,
		clearInlineErrorMessage,
	} = useAIEmailStepState({
		aiEmailStep,
		template,
		stepId: stepId || '',
		readonly,
		onSaveError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
	});

	const { save: persist } = useAIEmailStepSave({
		aiEmailStep,
		stepId: stepId || '',
		templateId: template?.id,
		name,
		prompt,
		tagToAddOnClick,
		skipStepContactFilter,
		autoSave,
	});

	const isPromptOverLimit = prompt.length > AI_EMAIL_PROMPT_MAX_LENGTH;

	const syncStepToNodeData = (updatedFields: Partial<Api.IAIEmailAutomationStep>) => {
		if (!updateNodeData || !aiEmailStep) return;
		updateNodeData(nodeId, {
			automationStep: { ...aiEmailStep, ...updatedFields },
		});
	};

	const handleNameChange = (newName: string) => {
		setName(newName);
		// Keep node header/compact title in sync while typing.
		syncStepToNodeData({ name: newName });
	};

	const handleNameBlur = () => {
		save();
		syncStepToNodeData({ name });
	};

	const handlePromptChange = (newPrompt: string) => {
		setPrompt(newPrompt);
		syncStepToNodeData({ prompt: newPrompt });
		clearInlineErrorMessage();
	};

	const promptField = useAIPromptField({
		onChange: handlePromptChange,
		// The save reads the prompt from step state, which by now holds `text`.
		onPromptApplied: ({ text, unfilledPlaceholder }) => {
			if (unfilledPlaceholder || text.length > AI_EMAIL_PROMPT_MAX_LENGTH) {
				return;
			}
			void persist();
			syncStepToNodeData({ prompt: text });
		},
		value: prompt,
	});

	// A PUT replaces the whole step, so an over-length prompt or an unfilled placeholder has to hold every save.
	const save = (overrides?: Parameters<typeof persist>[0]) => {
		if (isPromptOverLimit || promptField.unfilledPlaceholder) {
			return;
		}
		void persist(overrides);
	};

	const handlePromptBlur = () => {
		save();
		syncStepToNodeData({ prompt });
	};

	const handleTagToAddOnClickChange = (tag: string) => {
		setTagToAddOnClick(tag);
		save({ newTagToAddOnClick: tag });
		syncStepToNodeData({ tagToAddOnClick: tag });
	};

	const handleShowEmailLinkClickTagsChange = (show: boolean) => {
		setShowEmailLinkClickTags(show);
		if (!show) {
			handleTagToAddOnClickChange('');
		}
	};

	const handleContactFilterChange = (filter: Api.IContactsFilterRequest | null) => {
		setSkipStepContactFilter(filter);
		save({ newContactFilter: filter });
		syncStepToNodeData({ contactFilter: filter ?? undefined });
	};

	const handleSendFromChange = (sendFrom: Api.ISendFromOptions, sendToSender: boolean) => {
		save({ newSendFrom: sendFrom, newSendToSender: sendToSender });
		syncStepToNodeData({
			sendFromOptions: sendFrom,
			options: {
				...aiEmailStep?.options,
				sendToSender,
			},
		});
	};

	const handleScheduleChange = (newSchedule: Api.IAutomationStepSchedule | null) => {
		if (!newSchedule) return;
		save({ newSchedule });
		syncStepToNodeData({ schedule: newSchedule });
	};

	const behaviorConditionalOptions = useBehaviorConditionalOptions({
		nodeId,
		step: aiEmailStep as Api.IAutomationStep | null,
		template,
		readonly,
		parentCaseInfo: data.parentCaseInfo,
		impersonationContext,
	});

	const errorMessage = isPromptOverLimit
		? `The description must be ${AI_EMAIL_PROMPT_MAX_LENGTH} characters or fewer.`
		: showRequiredFieldError && (!name || !prompt.trim())
			? 'Please describe the email you want written, and give the step a name.'
			: null;
	const baseKey = `${stepType}-${nodeId}-${aiEmailStep?.id || ''}`;

	return (
		<div className={css(rendererContainer)}>
			<SaveStatus
				status={autoSave.saveStatus}
				variant='simple'
				retryCount={autoSave.retryCount}
				errorMessage={autoSave.errorMessage}
			/>
			{aiEmailStep && (
				<ScheduleEditor
					schedule={schedule}
					trigger={trigger}
					stepType={stepType}
					canEdit={canEdit}
					isBusy={autoSave.saveStatus === 'saving'}
					step={aiEmailStep}
					index={0}
					onScheduleChange={handleScheduleChange}
					parentCaseInfo={data.parentCaseInfo}
					impersonationContext={impersonationContext}
				/>
			)}
			<AIEmailStepForm
				name={name}
				prompt={prompt}
				promptField={promptField}
				canEdit={canEdit && !!aiEmailStep && !!template}
				isBusy={autoSave.saveStatus === 'saving'}
				baseKey={baseKey}
				errorMessage={errorMessage}
				impersonationContext={impersonationContext}
				onNameChange={handleNameChange}
				onNameBlur={handleNameBlur}
				onPromptBlur={handlePromptBlur}
				onClearError={clearInlineErrorMessage}
			/>
			<EmailLikeStepAdvancedOptions
				step={aiEmailStep || null}
				extraOptions={<ConditionalOptionsSection nodeId={nodeId} {...behaviorConditionalOptions} />}
				tagToAddOnClick={tagToAddOnClick}
				showEmailLinkClickTags={showEmailLinkClickTags}
				skipStepContactFilter={skipStepContactFilter}
				advancedOptionsToggled={advancedOptionsToggled}
				canEdit={canEdit}
				isBusy={autoSave.saveStatus === 'saving'}
				baseKey={baseKey}
				onToggleAdvancedOptions={setAdvancedOptionsToggled}
				onShowEmailLinkClickTagsChange={handleShowEmailLinkClickTagsChange}
				onTagToAddOnClickChange={handleTagToAddOnClickChange}
				onContactFilterChange={handleContactFilterChange}
				onSendFromChange={handleSendFromChange}
				impersonationContext={impersonationContext}
				connectionTypes={connectionTypes}
			/>
		</div>
	);
}
